import { Ref, useEffect, useState } from 'react'
import { InputBase, InputBaseProps } from '@mui/material'
import classNames from 'classnames'

import { Icon, IconName } from '../Icon'
import { Text } from '../Text'

import './index.scss'

export interface InputProps extends Omit<InputBaseProps, 'value' | 'ref'> {
  className?: string
  editable?: boolean
  icon?: IconName
  value?: string | number | null
  ref?: Ref<HTMLInputElement>
}
export interface FormattedInputProps extends Omit<InputProps, 'onChange'> {
  onAfterChange?: (value?: string) => void
}

const UneditableInput = ({ className, icon, value }: InputProps) => {
  return (
    <div className={classNames('populus-input uneditable', className)}>
      {icon && <Icon className="input-icon uneditable" color="black-1" icon={icon} />}
      <Text styleType="body">{value}</Text>
    </div>
  )
}

const Input = ({ className, editable = true, icon, ref, ...props }: InputProps) => {
  return editable ? (
    <InputBase
      fullWidth
      className={classNames('populus-input', icon && 'has-icon', className)}
      startAdornment={
        icon ? <Icon className="input-icon" color="black-1" icon={icon} /> : undefined
      }
      inputRef={ref}
      {...props}
    />
  ) : (
    <UneditableInput {...{ className, editable, icon, ...props }} />
  )
}

/**
 * An Input control for input that gets formatted after user entry. Stores the raw input and fires
 * onAfterChange when the input component loses focus, allowing the parent to format the value.
 */
export const FormattedInput = ({
  disabled,
  editable = true,
  onBlur,
  onAfterChange,
  value: formattedValue,
  ...props
}: FormattedInputProps) => {
  const [rawInputValue, setRawInputValue] = useState<string | undefined>('')

  // if "disabled" changes, discard the intermediate value
  useEffect(() => {
    setRawInputValue(undefined)
  }, [disabled])
  return (
    <Input
      data-testid="formatted-input"
      disabled={disabled}
      editable={editable}
      onFocus={() => {
        setRawInputValue(formattedValue != null ? String(formattedValue) : '')
      }}
      onBlur={e => {
        onAfterChange?.(rawInputValue || undefined)
        setRawInputValue(undefined)
        onBlur?.(e)
      }}
      onChange={e => {
        setRawInputValue(e.target.value)
      }}
      value={rawInputValue !== undefined ? rawInputValue : formattedValue}
      {...props}
    />
  )
}

Input.displayName = 'Input'

export { Input }
